import { ChangeDetectorRef, Component, CUSTOM_ELEMENTS_SCHEMA, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NzCollapseModule } from 'ng-zorro-antd/collapse';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzPaginationModule } from 'ng-zorro-antd/pagination';
import { Subscription } from 'rxjs';
import { subMonths } from 'date-fns';
import { Router, RouterModule } from '@angular/router';
import { HttpService } from '../../services/http.service';
import { StorageService } from '../../services/storage.service';
import { UserAccountDTO } from '../../classes/user-account.dto';

import { GtmService } from '../../services/gtm.service';

@Component({
  selector: 'app-symptom-assessment',
  templateUrl: './symptom-assessment.component.html',
  styleUrls: ['./symptom-assessment.component.scss'],
  imports: [TranslateModule, NzIconModule, NzGridModule, NzDividerModule, NzCollapseModule, NzPaginationModule, RouterModule],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class SymptomAssessmentComponent implements OnInit, OnDestroy {
  readonly NUMBER_OF_ACCORDIONS_SEEN = 5;
  accordionsPageIndex: number = 1;
  calculatedAccordionsList: Array<any> = [];
  filteredAccordionsList: Array<any> = [];
  subscriptions: Array<Subscription> = [];
  totalAccordions: number = 0;
  pageIndex: number = 1;
  pageSize: number = 5;
  consultType: number = 0; //all
  language: string = 'it';
  selectForm?: FormGroup;
  filteredAccordions: Array<any> = [];
  selectOptions = {
    medicalConsult: 1,
    symptomsEvaluation: 0,
    callmeBack: 2,
    careManager: 5,
    all: 4,
  };
  selectedOption: string | null = null;
  selectTexts: any;
  selectValues: any;
  response: any;

  constructor(
    private cdr: ChangeDetectorRef,
    private httpService: HttpService,
    private storage: StorageService,
    private fb: FormBuilder,
    private translateService: TranslateService,
    private gtmService: GtmService,
    private router: Router
  ) {}

  ngOnInit() {
    const initialFromDate = this.formatDate(subMonths(new Date(), 6));
    const initialToDate = this.formatDate(new Date());
    this.fetchAccordions(this.consultType, initialFromDate, initialToDate);

    this.selectForm = this.fb.group({
      options: [this.consultType, Validators.required],
      startDate: [subMonths(new Date(), 6)],
      endDate: [new Date()],
    });

    this.getTexts();

    this.subscriptions.push(
      this.translateService.onLangChange.subscribe(() => {
        this.getTexts();
      })
    );

    this.selectForm.valueChanges.subscribe((formValues) => {
      const consultType = formValues.options;
      const fromDate = this.formatDate(formValues.startDate);
      const toDate = this.formatDate(formValues.endDate);

      this.fetchAccordions(consultType, fromDate, toDate);
    });
  }

  private getTexts() {
    this.translateService.get('valutazioneSintomi').subscribe((translations) => {
      this.selectTexts = Object.keys(this.selectOptions).map((key) => translations[key]);
      this.selectValues = Object.keys(this.selectOptions).map((key) => this.selectOptions[key as keyof typeof this.selectOptions]);
    });
  }

  private fetchAccordions(consultType: number, fromDate: string, toDate: string): void {
    const userData = JSON.parse(this.storage.getItem('userData')!) as UserAccountDTO;
    const individualId = userData?.individualId;

    const payload = {
      individualId,
      consultType,
      pageIndex: this.pageIndex,
      pageSize: this.pageSize,
      fromDate,
      toDate,
      language: this.language,
    };

    this.httpService.getUserStory(payload.individualId, payload.consultType, payload.pageIndex, payload.pageSize, payload.fromDate, payload.toDate, payload.language).subscribe({
      next: (response) => {
        this.filteredAccordions = response.lista.filter((accordion: any) => {
          const consultDate = this.extractDate(accordion.consultDate);
          const startDate = parseInt(fromDate, 10);
          const endDate = parseInt(toDate, 10);

          const isDateInRange = consultDate >= startDate && consultDate <= endDate;
          // const isConsultTypeMatched = accordion.iConsultType === consultType;
          return isDateInRange; // && isConsultTypeMatched
        });

        this.calculatedAccordionsList = [...this.filteredAccordions];
        this.totalAccordions = this.filteredAccordions.length;
        this.calculateAccordionsElements(this.pageIndex);
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('Errore nel recupero dei dati:', error);
      },
    });
  }

  private extractDate(dateTime: string): number {
    const [datePart] = dateTime.split(' ');
    const [day, month, year] = datePart.split('/');
    return parseInt(`${year}${month}${day}`, 10);
  }

  private formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}${month}${day}`;
  }

  protected startValutation(): void {
    this.gtmService.pushSymptomAssessmentEvent('step0_start');
    this.router.navigate(['/symptom-assessment/new-valutation']);
  }

  protected changeAccordionsPage(page: number): void {
    this.calculateAccordionsElements(page);
  }

  private calculateAccordionsElements(page: number): void {
    const start = this.NUMBER_OF_ACCORDIONS_SEEN * (page - 1);
    const end = this.NUMBER_OF_ACCORDIONS_SEEN * page;

    this.calculatedAccordionsList = this.calculatedAccordionsList.slice(start, end);
    this.totalAccordions = this.calculatedAccordionsList.length;

    this.accordionsPageIndex = page;
    this.cdr.detectChanges();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription: Subscription) => {
      subscription.unsubscribe();
    });
  }
}
